import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage, ApiError } from "../../api/client";
import type { AttentionItem } from "./shared";

// Every register on the page, so a write that finds its record gone (deleted in another window) can refresh them.
const reloaders = new Set<() => Promise<void>>();

/** Loads one register's GET and keeps a reload; every write on the page calls `reload` after it. */
export function useRegister<T>(path: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      setData(await api.get<T>(path));
      setError(null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the register"));
    } finally {
      setLoading(false);
    }
  }, [path]);
  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    reloaders.add(reload);
    return () => { reloaders.delete(reload); };
  }, [reload]);
  return { data, loading, error, reload };
}

export interface MsOverview {
  enrolled: Array<{ id: string; label: string }>;
  relevant: Record<string, boolean>;
  counts: { context: number; objectives: number; audits: number; reviews: number; competence: number; aspects: number; hazards: number; obligations: number };
  attention: AttentionItem[];
}

/** The overview (attention items for every register), shared by each page's attention strip. */
export function useMsOverview() {
  return useRegister<MsOverview>("/management-system");
}

let writing = false;

/**
 * Runs a write and turns a refusal into a message, so forms show the API's own reason. One write at a
 * time: a second click on Save while the first is still on its way is ignored, never a second record.
 * A record that no longer exists (deleted in another window) is said so and the registers refresh.
 */
export async function attempt<T>(fn: () => Promise<T>, setError: (m: string | null) => void, fallback: string): Promise<T | null> {
  if (writing) return null;
  writing = true;
  try {
    const r = await fn();
    setError(null);
    return r;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      setError("That record no longer exists; it may have been deleted elsewhere. The list has been refreshed.");
      for (const reload of reloaders) void reload();
    } else {
      setError(apiErrorMessage(e, fallback));
    }
    return null;
  } finally {
    writing = false;
  }
}

/** A refusal belongs to the dialog that caused it: opening or closing any dialog clears it. */
export function useClearErrorOnDialog(setError: (m: string | null) => void, ...open: boolean[]) {
  const key = open.map(Boolean).join();
  useEffect(() => { setError(null); }, [key, setError]);
}
